import type { GroupedTechnologies } from "app/pages/sales-intelligence/modules/technology/types/technologies";
import { buildEntriesFromFilter } from "app/pages/sales-intelligence/sub-modules/signals/utils/technologies/buildEntriesFromFilter";
import type { SignalNameSegment } from "app/pages/sales-intelligence/sub-modules/signals/models/miscellaneous/SignalNameSegments";
import type TechnologySignalFilters from "app/pages/sales-intelligence/sub-modules/signals/models/technology/TechnologySignalFilters";
import TechnologySignalSubType from "app/pages/sales-intelligence/sub-modules/signals/models/technology/TechnologySignalSubType";
import { getTechnologiesFiltersValue } from "app/pages/sales-intelligence/sub-modules/signals/utils/technologies/filters";
import type { Translatable } from "app/pages/sales-intelligence/types";
import { createPluralHelperForI18nKey } from "app/pages/sales-intelligence/utils/strings";

import type Signal from "../../../models/core/Signal";
import type { TranslatableNameWithType } from "../../../models/services/FilterNamingService";
import { FilterType } from "../../../models/services/FilterService";
import filterService from "../../filters/filterService";
import { appendTimeframeSuffix } from "../shared/timeframeTranslatable";
import type UISignalNameBuilder from "../UISignalNameBuilder";
import {
    filterSelectionTranslatable,
    isPartialFilterName,
} from "app/pages/sales-intelligence/sub-modules/signals/models/services/filterSelectionNaming";
import { boldFilterSelectionTranslatable } from "app/pages/sales-intelligence/sub-modules/signals/utils/boldSignalValue";
import { selectionOverflowOnForDataSegment } from "app/pages/sales-intelligence/sub-modules/signals/utils/parseSignalDataSegment";

type FactoryDeps = {
    readonly technologies: GroupedTechnologies;
};

export default function createTechnologyUISignalNameBuilder(
    deps: FactoryDeps,
): UISignalNameBuilder<TechnologySignalFilters> {
    const buildTechnologyName = (
        filters: Readonly<Partial<TechnologySignalFilters>>,
    ): Readonly<TranslatableNameWithType> => {
        const nameBuilder = filterService.getFilterName(FilterType.Technologies);

        return nameBuilder.buildName(filters, deps.technologies);
    };

    const buildTypeSegment = (
        { signalType, signalSubType }: Signal<TechnologySignalFilters>,
        technologyName: Readonly<TranslatableNameWithType>,
    ): SignalNameSegment => {
        const signalI18nKeyTypeSegment = `si.signals.segments.${signalType}.${signalSubType}`;

        if (signalSubType === TechnologySignalSubType.CompetitorRenewalComingUp) {
            return { key: signalI18nKeyTypeSegment };
        }

        const getSingularI18nKey = createPluralHelperForI18nKey(signalI18nKeyTypeSegment);

        return {
            key: technologyName.type === "singular" ? getSingularI18nKey(1) : signalI18nKeyTypeSegment,
        };
    };

    const buildDataSegment = (
        { signalType, signalSubType, filters }: Signal<TechnologySignalFilters>,
        technologyName: Readonly<TranslatableNameWithType>,
    ): SignalNameSegment => {
        if (signalSubType === TechnologySignalSubType.CompetitorRenewalComingUp) {
            const signalI18nKeyDataSegment = `si.signals.segments.data.${signalType}.${signalSubType}`;

            return {
                key: signalI18nKeyDataSegment,
                replacements: {
                    technologies: filterSelectionTranslatable(technologyName),
                    periodAmount: filters.periodAmount,
                },
            };
        }

        return appendTimeframeSuffix(filterSelectionTranslatable(technologyName), filters);
    };

    return {
        buildNameSegments(signal: Signal<TechnologySignalFilters>) {
            const technologyName = buildTechnologyName(signal.filters);
            const signalData = buildDataSegment(signal, technologyName);

            return {
                signalType: buildTypeSegment(signal, technologyName),
                signalData,
                ...(isPartialFilterName(technologyName)
                    ? { selectionOverflowOn: selectionOverflowOnForDataSegment(signalData) }
                    : {}),
            };
        },
        buildName({ signalType, signalSubType, filters }: Signal<TechnologySignalFilters>): Readonly<Translatable> {
            const signalI18nKey = `si.signals.${signalType}.${signalSubType}`;
            const getSingularI18nKey = createPluralHelperForI18nKey(signalI18nKey);
            const technologyName = buildTechnologyName(filters);

            const baseName: Translatable = {
                key: technologyName.type === "singular" ? getSingularI18nKey(1) : signalI18nKey,
                replacements: {
                    technologies: boldFilterSelectionTranslatable(technologyName),
                    periodAmount: filters.periodAmount,
                },
            };

            if (signalSubType === TechnologySignalSubType.CompetitorRenewalComingUp) {
                return baseName;
            }

            return appendTimeframeSuffix(baseName, filters);
        },
        buildSelectionTooltipLabels(signal: Signal<TechnologySignalFilters>) {
            const technologyName = buildTechnologyName(signal.filters);

            if (!isPartialFilterName(technologyName)) {
                return undefined;
            }

            const labels = buildEntriesFromFilter(getTechnologiesFiltersValue(signal.filters)).map(({ name }) => name);

            return labels.length > 1 ? { signalData: labels } : undefined;
        },
    };
}
